import { Microfrontend } from '@cian/mf-react';
import { type IMicrofrontendManifest } from '@cian/mf-registry/shared';

import * as React from 'react';

import { AdBannerPlaceholder } from './components/AdBannerPlaceholder';
import { useAdBannerCommonParameters } from './hooks/useAdBannerCommonParameters';
import { useAdBannerMicrofrontend } from './hooks/useAdBannerMicrofrontend';
import { useAdBannerMicrofrontendEvents } from './hooks/useAdBannerMicrofrontendEvents';
import { TAdBannerPlaceParameters } from './types';
import { useApplicationContext } from '../../utils/applicationContext';
import { AD_BANNER_MF_MANIFEST_SETTING_KEY } from '../../constants/microfrontend';

type TMicrofrontendParameters = React.ComponentProps<typeof Microfrontend>['parameters'];

interface IAdBannerMicrofrontendContainerProps {
  parameters: TAdBannerPlaceParameters;
  /** Вызывается на заглушке/ошибке (lifecycle `stub`/`error`). */
  onAdStub?(): void;
  /** Вызывается на ошибке (lifecycle `error`). */
  onAdError?(): void;
}

/**
 * Дженерик-контейнер микрофронтенда баннера: ничего не знает о конкретном месте.
 * Резолвит общие параметры (`puids`, `regionId`), держит размер плейсхолдером и
 * монтирует `<Microfrontend>` строго на клиенте, мерджа place- и общие параметры.
 * Конкретное место задаётся через `parameters` (см. `places.ts`).
 */
export const AdBannerMicrofrontendContainer: React.FC<IAdBannerMicrofrontendContainerProps> = ({
  parameters,
  onAdStub,
  onAdError,
}) => {
  const { config } = useApplicationContext();

  const commonParameters = useAdBannerCommonParameters();

  const { microfrontendRef, isReady } = useAdBannerMicrofrontend();

  const manifest = config.get<IMicrofrontendManifest>(AD_BANNER_MF_MANIFEST_SETTING_KEY);

  useAdBannerMicrofrontendEvents({ placeId: commonParameters.placeId, isReady, onAdStub, onAdError });

  const mergedParameters = React.useMemo(
    (): TMicrofrontendParameters => ({ ...commonParameters, ...parameters }),
    [parameters, commonParameters],
  );

  if (!manifest) {
    return null;
  }

  const { microfrontendName, imageVersion, runtimeName } = manifest;

  return (
    <AdBannerPlaceholder sizes={parameters.sizes} testId={`AdBannerMicrofrontend-${parameters.place}`}>
      {isReady && (
        <Microfrontend
          ref={microfrontendRef}
          name={microfrontendName}
          image={imageVersion}
          runtime={runtimeName}
          parameters={mergedParameters}
        />
      )}
    </AdBannerPlaceholder>
  );
};
